import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/DropdownMenu';
import {
  ComposerIcon,
  DownloadIcon,
  DownloadStemsIcon,
  EditFilledIcon,
  EllipsisColIcon,
  MoveFolderIcon,
  PublishedIcon,
  ShareIcon,
  TrashIcon,
} from '@/components/Icons';
import { getAudioDetail } from '@/service/audioList';
import { useDeleteTrack } from '@/hooks/useDeleteTrack';
import { useDimensions } from '@/hooks/useDimensions';
import { useDownloadAudio, useDownloadStemsAudio } from '@/hooks/useDownloadAudio';
import { useMobileEditorDialog } from '@/hooks/useMobileEditorDialog';
import useMoveTrackToFolderDialog from '@/hooks/useMoveTrackToFolderDialog';
import { usePublishDialog } from '@/hooks/usePublishDialog';
import { AudioDto } from '@/service/type';
import { useSongDetailDrawer } from '@/state/drawer-store';
import useGeneratorStore, { GeneratorTypeEnums } from '@/state/generator-store';
import { useNameChangeInputShow } from '@/state/namechange-store';
import { useSubscriptionStore } from '@/state/subsription-store';
import { cn } from '@/utils';
import React from 'react';
import { toast } from 'sonner';
import { useClipboard } from 'use-clipboard-copy';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { useController } from 'react-hook-form';
import { isEmpty } from 'lodash-es';
import { findGroupIndexBySfCode, findSfListIndexByCode } from '../SongDetail/TrackControl';

export const AudioActionEllipseDropDown: React.FC<{
  hide?: boolean;
  className?: string;
  audioInfo: AudioDto;
  onSelect: (audioInfo: AudioDto) => void;
}> = ({ hide, className, audioInfo, onSelect }) => {
  const id = audioInfo.id;
  const showMoveDialog = useMoveTrackToFolderDialog({ id });
  const { onShowPublishDialog, onShowPrivateDialog } = usePublishDialog({ id });
  const { setIsNameInputShow } = useNameChangeInputShow();
  const { showDeleteDialog } = useDeleteTrack({ id });
  const { onShowEditorDialog } = useMobileEditorDialog({ id });

  const { isMobileOrPad } = useDimensions();
  const { permission } = useSubscriptionStore();
  const { setIsDrawerShow } = useSongDetailDrawer();
  const clipboard = useClipboard();
  const { download } = useDownloadAudio();
  const { downloadStems } = useDownloadStemsAudio();

  const { setGeneratorType, setIsGeneratorDrawerShow, setAudioInfo, control, formValues } = useGeneratorStore((state) => ({
    setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
    setGeneratorType: state.setGeneratorType,
    setAudioInfo: state.setAudioInfo,
    control: state.control,
    formValues: state.formValues,
  }));

  const {
    field: { onChange: setStyleFilterId },
  } = useController({
    name: 'styleFilterId',
    control,
  });

  const {
    field: { onChange: setStyleFilterIsOpened },
  } = useController({
    name: 'styleFilterIsOpened',
    control,
  });

  const {
    field: { onChange: setStyleFilterWeight },
  } = useController({
    name: 'filterWeight',
    control,
    defaultValue: formValues?.filterWeight || undefined,
  });
  const { filterGroupList, setCurrentFilterGroup, setDisabled } = useGeneratorFilterStore();

  const { pause } = useAudioPlayerStore();

  if (hide)
    return (
      <div>
        <EllipsisColIcon
          fillColor="currentColor"
          className={cn(className, 'cursor-pointer', {
            invisible: hide,
          })}
        />
      </div>
    );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="outline-none">
          <div>
            <EllipsisColIcon
              fillColor={isMobileOrPad ? '#48484B' : 'currentColor'}
              className={cn(className, 'cursor-pointer hover:text-jen-black-100')}
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className=" bg-white  text-black text-[14px] " onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onClick={() => {
              onSelect(audioInfo);
              setIsDrawerShow(true);
              setTimeout(() => {
                setIsNameInputShow(true);
              }, 100);
            }}
            className=" cursor-pointer"
          >
            <EditFilledIcon />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              if (audioInfo.is_published) {
                clipboard.copy(`${window.location.origin}/s/${id}`);
                toast('Link Copied to Clipboard');
              } else {
                onShowPublishDialog({
                  name: audioInfo.name,
                  createAt: audioInfo.created_at,
                  duration: audioInfo.duration,
                });
              }
            }}
            className=" cursor-pointer"
          >
            <ShareIcon />
            Share
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              clipboard.copy(`${window.location.origin}/private-s/${id}`);
              toast('Link Copied to Clipboard');
            }}
            className=" cursor-pointer"
          >
            <ShareIcon />
            Private Share
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              audioInfo?.is_published
                ? onShowPrivateDialog()
                : onShowPublishDialog({
                    name: audioInfo.name,
                    duration: audioInfo.duration,
                    createAt: audioInfo.created_at,
                  });
            }}
            className=" cursor-pointer"
          >
            <PublishedIcon />
            {audioInfo?.is_published ? 'Published' : 'Publish'}
          </DropdownMenuItem>
          <div className="border-b border-jen-border"></div>
          <DropdownMenuItem
            onClick={() => {
              showMoveDialog();
            }}
            className=" cursor-pointer"
          >
            <MoveFolderIcon />
            Move to Folder
          </DropdownMenuItem>
          <DropdownMenuItem
            className=" cursor-pointer"
            onClick={async () => {
              pause();
              if (isEmpty(audioInfo.stylefilterDetail) || !audioInfo.stylefilterDetail) {
                setDisabled(true);
                setCurrentFilterGroup(null);
                setStyleFilterIsOpened(false);
              } else {
                const groupIndex = findGroupIndexBySfCode(filterGroupList, audioInfo.stylefilterDetail?.code);
                if (groupIndex === -1) {
                  toast('style filter not supported');
                } else if (filterGroupList && filterGroupList[groupIndex]) {
                  const sfListIndex = findSfListIndexByCode(filterGroupList[groupIndex], audioInfo.stylefilterDetail?.code);
                  if (sfListIndex === -1) {
                    toast('style filter not supported');
                  }
                  setCurrentFilterGroup(filterGroupList[groupIndex]);
                  setStyleFilterId(filterGroupList[groupIndex].sf_list[sfListIndex].id);
                  setStyleFilterWeight(sfListIndex === 0 ? 0 : 1);
                  setStyleFilterIsOpened(true);
                }
              }
              if (isMobileOrPad) {
                onShowEditorDialog();
              } else {
                if ('source_url' in audioInfo && audioInfo?.source_url) {
                  setAudioInfo(audioInfo);
                } else {
                  const audioDetail = await getAudioDetail({ id: audioInfo.id });
                  setAudioInfo(Object.assign({}, audioInfo, audioDetail.data));
                }
                setIsGeneratorDrawerShow(true);
                setGeneratorType(GeneratorTypeEnums.EDITOR);
              }
            }}
          >
            <ComposerIcon />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!(audioInfo.is_generated || audioInfo.status === 'generated' || audioInfo.status === 'validated')}
            onClick={() => {
              download(audioInfo);
            }}
            className=" cursor-pointer"
          >
            <DownloadIcon />
            Download
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={
              !(audioInfo.is_generated || audioInfo.status === 'generated' || audioInfo.status === 'validated') ||
              permission.downloadStems === null
            }
            className={cn(' cursor-pointer')}
            onClick={() => {
              downloadStems(audioInfo);
            }}
          >
            <DownloadStemsIcon fillColor="currentColor" />
            Download Stems
            {permission.downloadStems === null && <UpgradeTag />}
          </DropdownMenuItem>

          <div className="border-b border-jen-border"></div>
          <DropdownMenuItem
            onClick={() => {
              showDeleteDialog();
            }}
            className="text-jen-negative-sentiment cursor-pointer"
          >
            <TrashIcon fillColor="currentColor" />
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const UpgradeTag = () => {
  return (
    <div className=" font-pressura-mono ml-[4px] h-[16px] bg-jen-background-secondary border border-jen-border leading-[16px] rounded-[2px] px-[8px]  text-[12px] text-jen-content-tertiary">
      UPGRADE
    </div>
  );
};
